import { Component, EventEmitter, OnInit, Output, Input, OnChanges, SimpleChanges } from "@angular/core";
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from "@angular/forms";
import { faGrinTongueSquint } from "@fortawesome/free-solid-svg-icons";
import { toJSDate } from "@ng-bootstrap/ng-bootstrap/datepicker/ngb-calendar";
import * as moment from "moment";
import { Observable } from "rxjs";
import Swal from "sweetalert2";
import { OptionsReferenceDteEmitido } from "src/app/models/Dte/OptionsReferenceDteEmitido";
import { ReferenciaDte } from "src/app/models/Dte/ReferenciaDte";
import { ReferenciaDteEmitido } from "src/app/models/Dte/ReferenciaDteEmitido";
import { TipoReferenciaEnum } from "src/app/models/Dte/TipoReferencia.enum";
import { TipoDocumentoReferencia } from "src/app/models/TipoDocRef";
import { TipoDte } from "src/app/models/tipoDte";
import { FacturacionIndividualService } from "src/app/services/facturacion-individual.service";
import { LocalStorageService } from "src/app/services/local-storage.service";
import { InvoiceType } from "src/app/models/Dte/invoiceType.enum";

@Component({
	selector: "app-references-list",
	templateUrl: "./references-list.component.html",
	styleUrls: ["./references-list.component.scss"],
})
export class ReferencesListComponent implements OnInit, OnChanges {
	public referencesForm: FormGroup;
	public referencesArray: FormArray;
	private referencesList: ReferenciaDteEmitido[] = [];
	public tipoDteReferenciaSource: TipoDocumentoReferencia[];
	public tipoReferenciaEnumSource: any[] = [];
	@Input() invoiceType: InvoiceType;
	@Input() initialReferences: ReferenciaDteEmitido[] = [];
	private pendingInitialReferences: ReferenciaDteEmitido[] = [];
	private lastAppliedInitialReferencesKey: string;
	private readonly fechasIniciales = new WeakMap<FormGroup, string>();

	public validDatesArray: boolean[] = [];
	locale = {
		daysOfWeek: moment.weekdaysMin(),
		monthNames: moment.monthsShort(),
		firstDay: moment.localeData().firstDayOfWeek(),
		applyLabel: "ok",
		clearLabel: "Limpiar",
		format: "DD/MM/YYYY",
	};
	@Output() referencesListEmitter: EventEmitter<ReferenciaDteEmitido[]> = new EventEmitter<ReferenciaDteEmitido[]>();
	private referencesFromGuides: ReferenciaDteEmitido[] = [];
	//private currentRefsFromGuide:ReferenciaDteEmitido[] = [];
	private refFromGuides$: Observable<ReferenciaDteEmitido[]>;
	private opcionesRefDte$: Observable<OptionsReferenceDteEmitido>;
	public globalReferenceForGuideRefs: boolean = false;
	constructor(private formBuilder: FormBuilder, private localStorage: LocalStorageService, private facturador: FacturacionIndividualService) {
		this.refFromGuides$ = this.facturador.referencesListFromGuidesObservable;
		this.opcionesRefDte$ = this.facturador.opcionesReferenciaObservable;
	}

	ngOnInit(): void {
		this.tipoDteReferenciaSource = JSON.parse(this.localStorage.getTipoDocReferencias()) as TipoDocumentoReferencia[];
		this.tipoReferenciaEnumSource = Object.keys(TipoReferenciaEnum).map((key) => ({ key, name: TipoReferenciaEnum[key] }));
		this.tipoReferenciaEnumSource = this.tipoReferenciaEnumSource.splice(0, 3);
		this.setReferencesForm();
		this.applyInitialReferences(this.initialReferences);
		this.refFromGuides$.subscribe((res) => {
			if (res && res.length > 0) {
				this.referencesFromGuides = res;
				this.passGuideRefToReferenceFormGroup(this.referencesFromGuides);
			}
		});
		this.opcionesRefDte$.subscribe((res) => {
			this.globalReferenceForGuideRefs = res.globalReferenceForGuideRefs;
		});
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes["initialReferences"]) {
			this.applyInitialReferences(changes["initialReferences"].currentValue ?? []);
		}
		if (changes["invoiceType"] && this.referencesForm) {
			(this.referencesForm.controls.referencesArray as FormArray).controls.forEach(control => this.actualizarValidadoresReferencia(control as FormGroup));
		}
	}

	setReferencesForm() {
		this.referencesForm = this.formBuilder.group({
			referencesArray: this.formBuilder.array([this.createReferenceItemForm()]),
		});
	}

	private applyInitialReferences(list: ReferenciaDteEmitido[]): void {
		this.pendingInitialReferences = list ?? [];
		if (!this.referencesForm) {
			return;
		}

		const nextKey = this.getReferencesKey(this.pendingInitialReferences);
		if (nextKey === this.lastAppliedInitialReferencesKey) {
			return;
		}

		this.setReferencesFromDraft(this.pendingInitialReferences);
		this.lastAppliedInitialReferencesKey = nextKey;
	}

	private getReferencesKey(list: ReferenciaDteEmitido[]): string {
		return JSON.stringify(
			(list ?? []).map((item) => ({
				codigoTipoDteReferencia: item?.codigoTipoDteReferencia ?? "",
				folioReferencia: item?.folioReferencia ?? "",
				fechaDteReferenciado: item?.fechaDteReferenciado ? moment(item.fechaDteReferenciado).format("YYYY-MM-DD") : "",
				razonReferencia: item?.razonReferencia ?? "",
			}))
		);
	}

	createReferenceItemForm(): FormGroup {
		this.validDatesArray.push(true);
		const group = this.formBuilder.group({
			tipoDte: [null],
			rutEmisor: [""],
			folioRef: ["", [Validators.maxLength(18)]],
			fechaRef: [
				{
					startDate: moment().toDate(),
					endDate: moment().toDate(),
				},
			],
			razonRef: [""],
		});
		group.get("tipoDte").valueChanges.subscribe((newTipoDte) => {
			this.validateUniqueDteReference(group, newTipoDte);
		});
		this.fechasIniciales.set(group, moment(group.controls.fechaRef.value.startDate).format("YYYY-MM-DD"));
		group.valueChanges.subscribe(() => this.actualizarValidadoresReferencia(group));
		this.actualizarValidadoresReferencia(group);

		return group;
	}

	private tieneDatosReferencia(group: FormGroup): boolean {
		const { tipoDte, folioRef, rutEmisor, razonRef, fechaRef } = group.getRawValue();
		const fecha = fechaRef?.startDate;
		const fechaModificada = !!fecha && moment(fecha, "DD-MM-YYYY").format("YYYY-MM-DD") !== this.fechasIniciales.get(group);
		return !!tipoDte || [folioRef, rutEmisor, razonRef].some(value => String(value ?? "").trim().length > 0) || fechaModificada;
	}

	private actualizarValidadoresReferencia(group: FormGroup): void {
		const obligatoria = this.invoiceType === InvoiceType.NotaCredito || this.invoiceType === InvoiceType.NotaDebito || this.tieneDatosReferencia(group);
		group.controls.tipoDte.setValidators(obligatoria ? [Validators.required] : []);
		group.controls.folioRef.setValidators(obligatoria ? [Validators.required, Validators.maxLength(18), Validators.pattern(/\S/)] : [Validators.maxLength(18)]);
		group.controls.tipoDte.updateValueAndValidity({ emitEvent: false });
		group.controls.folioRef.updateValueAndValidity({ emitEvent: false });
	}
	isDteTributario(codigo: string): boolean {
		return !!codigo && !isNaN(Number(codigo)) && Number(codigo) < 800 && Number(codigo) !== 48;
	}

	private validateUniqueDteReference(currentGroup: FormGroup, selectedTipoDte: any) {
		if (!selectedTipoDte) return;

		if (this.invoiceType !== InvoiceType.NotaCredito && this.invoiceType !== InvoiceType.NotaDebito) return;

		const selectedCode = selectedTipoDte.codigo;
		if (!this.isDteTributario(selectedCode)) return;

		const currentRefsArray = this.referencesForm.controls.referencesArray as FormArray;
		const count = currentRefsArray.controls.filter((result) => {
			if (result === currentGroup) return false;
			const tipoDte = result.get("tipoDte").value;
			return tipoDte && this.isDteTributario(tipoDte.codigo);
		}).length;

		if (count >= 1) {
			currentGroup.get("tipoDte").setValue(null);
			Swal.fire({
				icon: "error",
				title: "Una Referencia DTE por documento",
				text: "En una Nota de Crédito/Débito solo puede referenciar a un DTE (Documento Tributario Electrónico).",
			});
		}
	}

	addReferenceFormItem() {
		const currentRefsArray = this.referencesForm.controls.referencesArray as FormArray;

		if (currentRefsArray.length < 5) {
			let newReferenceItemForm = this.createReferenceItemForm();
			currentRefsArray.push(newReferenceItemForm);
			//this.checkIfReferenceDocumentTypeIsSelected(61);
		}
	}

	removeReferenceFormItem(index) {
		const currentArray = this.referencesForm.controls.referencesArray as FormArray;
		currentArray.removeAt(index);
		this.validDatesArray.splice(index, 1);
		if (currentArray.length === 0) currentArray.push(this.createReferenceItemForm());
		this.onSubmit();
	}

	onDateChange(event, i) {
		this.detectChangeReferenceDate(event, i);
		this.onSubmit();
	}

	onSubmit() {
		const currentArray = this.referencesForm.controls.referencesArray as FormArray;
		this.referencesList = [];
		currentArray.controls.forEach((control, index) => {
			const group = control as FormGroup;
			this.actualizarValidadoresReferencia(group);
			if (!this.tieneDatosReferencia(group)) return;
			const referencia = this.getReferenceItemData(group);
			referencia.isValid = group.valid && this.validDatesArray[index] !== false && !!referencia.fechaDteReferenciado && moment(referencia.fechaDteReferenciado).isValid();
			this.referencesList.push(referencia);
		});
		// Publicar también las filas incompletas y la lista vacía evita conservar datos anteriores.
		this.referencesListEmitter.emit(this.referencesList);
	}

	// checkIfReferenceDocumentTypeIsSelected(codeSii:number){
	//   //Disable TipoRazonRef control based on tipoDte Selected code
	//   (this.referencesForm.controls.referencesArray as FormArray).controls.forEach(control => {
	//     let tipoDte:TipoDocumentoReferencia = (control as FormGroup).controls.tipoDte.value as TipoDocumentoReferencia;
	//     if(!tipoDte || tipoDte.codigo != codeSii){
	//       (control as FormGroup).controls.tipoRazonRef.patchValue(undefined);
	//       (control as FormGroup).controls.tipoRazonRef.disable();
	//     }else{
	//       (control as FormGroup).controls.tipoRazonRef.setValidators([Validators.required]);
	//     }
	//   });
	// }

	getReferenceItemData(referenceItemForm: FormGroup): ReferenciaDteEmitido {
		let ref: ReferenciaDteEmitido = new ReferenciaDteEmitido();
		let tipoDteSelected: TipoDocumentoReferencia = referenceItemForm.controls.tipoDte.value;
		ref.codigoTipoDteReferencia = tipoDteSelected ? tipoDteSelected.codigo : "0";
		ref.folioReferencia = referenceItemForm.controls.folioRef.value;
		const fecha = referenceItemForm.controls.fechaRef.value?.startDate;
		ref.fechaDteReferenciado = fecha ? moment(fecha, "DD-MM-YYYY").toDate() : undefined;
		ref.razonReferencia = referenceItemForm.controls.razonRef.value;
		return ref;
	}

	// detectChangeReferenceDate(event, i) {
	//    if (event.startDate != null) {
	//       this.referencesForm.get("referencesArray")["controls"][i].controls.fechaRef.patchValue(event);
	//    } else if (event.target != null && event.target.value != null) {
	//       this.validDatesArray[i] = moment(event.target.value, "DD/MM/YYYY", true).isValid();
	//       if (this.validDatesArray[i]) {
	//          let obj: any = new Object();
	//          obj.startDate = moment(event.target.value, "DD-MM-YYYY");
	//          this.referencesForm.get("referencesArray")["controls"][i].controls.fechaRef.patchValue(obj);
	//       }
	//    }
	// }

	detectChangeReferenceDate(event, i) {
		if (event.startDate != null) {
			this.validDatesArray[i] = moment(event.startDate).isValid();
			const fecha = { startDate: moment(event.startDate).format("DD-MM-YYYY") };
			this.referencesForm.get("referencesArray")["controls"][i].controls.fechaRef.patchValue(fecha);
		} else if (event.target != null && event.target.value != null) {
			this.validDatesArray[i] = moment(event.target.value, "DD/MM/YYYY", true).isValid();
			if (this.validDatesArray[i]) {
				const fecha = { startDate: moment(event.target.value, "DD-MM-YYYY").format("DD-MM-YYYY") };
				this.referencesForm.get("referencesArray")["controls"][i].controls.fechaRef.patchValue(fecha);
			}
		}
	}

	passGuideRefToReferenceFormGroup(list: ReferenciaDteEmitido[]) {
		let formsArray = this.referencesForm.controls.referencesArray as FormArray;
		formsArray.clear();
		this.validDatesArray = [];
		list.forEach((ref, i) => {
			this.addReferenceFormItem();
			this.guideRefToFormItem(ref, i);
		});
		this.onSubmit();
	}

	guideRefToFormItem(guideRef: ReferenciaDteEmitido, index: number) {
		let formsArray = this.referencesForm.controls.referencesArray as FormArray;
		if (formsArray.controls[index] as FormGroup) {
			this.updateFormControlsWithReference(formsArray.controls[index] as FormGroup, guideRef);
		} else {
			this.addReferenceFormItem();
			this.updateFormControlsWithReference(formsArray.controls[index] as FormGroup, guideRef);
		}
	}

	updateFormControlsWithReference(form: FormGroup, item: ReferenciaDteEmitido) {
		form.controls.folioRef.patchValue(item.folioReferencia);
		let dateObj = {
			startDate: moment(item.fechaDteReferenciado).toDate(),
			endDate: moment().toDate(),
		};
		form.controls.fechaRef.patchValue(dateObj);
		form.controls.razonRef.patchValue(item.razonReferencia);
		let tipoDoc = this.tipoDteReferenciaSource.find((tipo) => tipo.codigo == item.codigoTipoDteReferencia);
		form.controls.tipoDte.patchValue(tipoDoc);
	}

	setReferencesFromDraft(list: ReferenciaDteEmitido[]): void {
		const referencesArray = this.referencesForm.controls.referencesArray as FormArray;
		while (referencesArray.length > 0) {
			referencesArray.removeAt(0);
		}
		this.referencesList = [];
		this.validDatesArray = [];

		if (!list || list.length === 0) {
			referencesArray.push(this.createReferenceItemForm());
			return;
		}

		list.forEach((item) => {
			const control = this.createReferenceItemForm();
			referencesArray.push(control);
			this.updateFormControlsWithReference(control, item);
			this.referencesList.push(Object.assign(new ReferenciaDteEmitido(), item));
		});
	}
}
